import AppCard from '@nxsys/components/AppCard';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useGetPayBreakdown,
  useGetPayrollPeriods,
} from '@nxsys/hooks/api/dashboard/useDashboardApi';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Checkbox, Skeleton } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getBreakdownColor } from './payBreakdownColors';
import PayBreakdownChart, { PayBreakdownItem } from './PayBreakdownChart';

type Props = {
  isOpen: boolean;
  toggle: () => void;
};

const PayBreakdownModal: React.FC<Props> = ({ isOpen, toggle }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);

  const {
    value: periodsValue,
    loading: periodsLoading,
    run: fetchPeriods,
  } = useGetPayrollPeriods();

  const {
    value: breakdownValue,
    loading: breakdownLoading,
    run: fetchBreakdown,
  } = useGetPayBreakdown();

  const [selectedTaxYear, setSelectedTaxYear] = useState<number | null>(null);
  const [selectedTaxPeriod, setSelectedTaxPeriod] = useState<number | null>(
    null,
  );
  const [isYearToDate, setIsYearToDate] = useState(false);

  const periodsData = periodsValue?.data ?? null;
  const allPayrollPeriods = periodsData?.allPayrollPeriods ?? [];
  const defaultPayrollPeriod = periodsData?.defaultPayrollPeriod ?? null;

  // Fetch periods when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPeriods();
    }
  }, [isOpen]);

  // Set defaults when periods data arrives
  useEffect(() => {
    if (defaultPayrollPeriod && selectedTaxYear === null) {
      setSelectedTaxYear(defaultPayrollPeriod.taxYear);
      setSelectedTaxPeriod(defaultPayrollPeriod.taxPeriod);
    }
  }, [defaultPayrollPeriod]);

  // Fetch breakdown when filters change
  const fetchBreakdownData = useCallback(() => {
    if (!selectedTaxYear || !selectedTaxPeriod) return;

    if (isYearToDate && defaultPayrollPeriod) {
      const yearData = allPayrollPeriods.find(
        (p) => p.taxYear === defaultPayrollPeriod.taxYear,
      );
      if (yearData) {
        fetchBreakdown({
          taxYear: defaultPayrollPeriod.taxYear.toString(),
          taxPeriods: yearData.taxPeriods,
        });
      }
    } else {
      fetchBreakdown({
        taxYear: selectedTaxYear.toString(),
        taxPeriods: [selectedTaxPeriod],
      });
    }
  }, [selectedTaxYear, selectedTaxPeriod, isYearToDate, defaultPayrollPeriod]);

  useEffect(() => {
    if (isOpen && selectedTaxYear && selectedTaxPeriod) {
      fetchBreakdownData();
    }
  }, [selectedTaxYear, selectedTaxPeriod, isYearToDate, isOpen]);

  // Get available tax periods for selected year
  const availableTaxPeriods =
    allPayrollPeriods.find((p) => p.taxYear === selectedTaxYear)?.taxPeriods ??
    [];

  // Handle tax year change
  const handleTaxYearChange = (option: { label: string; value: any }) => {
    const newYear = Number(option.value);
    setSelectedTaxYear(newYear);

    const yearData = allPayrollPeriods.find((p) => p.taxYear === newYear);
    if (
      yearData &&
      selectedTaxPeriod &&
      !yearData.taxPeriods.includes(selectedTaxPeriod)
    ) {
      setSelectedTaxPeriod(yearData.taxPeriods[0] ?? null);
    }
  };

  // Handle tax period change
  const handleTaxPeriodChange = (option: { label: string; value: any }) => {
    setSelectedTaxPeriod(Number(option.value));
  };

  // Map API data to chart format
  const apiData = breakdownValue?.data ?? null;
  const breakdownData: PayBreakdownItem[] =
    apiData?.breakdowns?.map((item, index) => ({
      name: item.name,
      value: item.amount,
      color: getBreakdownColor(item.key, index),
      percentage: item.percentage,
    })) ?? [];

  const total = apiData?.total?.amount ?? 0;
  const totalLabel = apiData?.total?.name ?? '';

  // Dropdown options
  const taxYearOptions = allPayrollPeriods.map((p) => ({
    label: `${p.taxYear}/${p.taxYear + 1}`,
    value: p.taxYear.toString(),
  }));

  const taxPeriodOptions = availableTaxPeriods.map((period) => ({
    label: `${messages['common.taxWeek']} ${period}`,
    value: period.toString(),
  }));

  const columns: ColumnsType<PayBreakdownItem> = [
    {
      title: messages['common.item'],
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: messages['common.amount'],
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (value) =>
        formatMoney(value, showCurrencyByCountry(payrollCountry)),
    },
    {
      title: messages['common.percentage'],
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      render: (value) => <div className="text-primary-600">{value}%</div>,
    },
  ];

  const isFilterDisabled = periodsLoading || isYearToDate;

  return (
    <AppModalContainer
      title={messages['dashboard.payBreakdown']}
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      minHeight="0"
      noPaddingBody
    >
      <AppCard
        title={
          <div className="flex items-center justify-between w-full gap-x-[8px] flex-wrap">
            <div className="flex items-center gap-x-[8px]">
              <AppSelect
                placeholder={messages['common.taxYear']}
                value={
                  selectedTaxYear
                    ? {
                        label: `${selectedTaxYear}/${selectedTaxYear + 1}`,
                        value: selectedTaxYear.toString(),
                      }
                    : undefined
                }
                onChange={handleTaxYearChange}
                options={taxYearOptions}
                disabled={isFilterDisabled}
                className="min-w-[150px]"
                allowClear={false}
              />
              <AppSelect
                placeholder={messages['common.taxWeek']}
                value={
                  selectedTaxPeriod
                    ? {
                        label: `${messages['common.taxWeek']} ${selectedTaxPeriod}`,
                        value: selectedTaxPeriod.toString(),
                      }
                    : undefined
                }
                onChange={handleTaxPeriodChange}
                options={taxPeriodOptions}
                disabled={isFilterDisabled}
                className="min-w-[150px]"
                allowClear={false}
              />
            </div>
            <Checkbox
              checked={isYearToDate}
              onChange={(e) => setIsYearToDate(e.target.checked)}
              disabled={periodsLoading}
            >
              {messages['dashboard.payBreakdown.yearToDate']}
            </Checkbox>
          </div>
        }
        className="!border-none pb-6"
      >
        <div className="pt-[16px] px-[24px]">
          <div className="text-[12px] font-[400] text-neutral-700 mb-[16px]">
            {messages['dashboard.payBreakdown.description']}
          </div>
          {breakdownLoading && !apiData ? (
            <Skeleton active />
          ) : breakdownData.length > 0 ? (
            <PayBreakdownChart
              data={breakdownData}
              total={total}
              totalLabel={totalLabel}
            />
          ) : (
            <div className="text-center text-neutral-500 py-[32px]">
              {messages['common.noData']}
            </div>
          )}
        </div>
        <AppTableContainer
          rowKey="name"
          data={breakdownData}
          loading={breakdownLoading && !apiData}
          page={1}
          total={breakdownData.length}
          pageSize={10}
          pagination={false}
          scroll={{ x: 300 }}
          columns={columns}
        />
      </AppCard>
    </AppModalContainer>
  );
};

export default PayBreakdownModal;
